/* eslint-disable max-len */
/* eslint-disable react/no-unknown-property */
import React from "react";

import { SVGIconTheMuseLogo } from "@advice/components/atoms/SVGIconTheMuseLogo";
import { ArticleContent } from "@advice/hooks/ArticleContent";
import { Configuration } from "@advice/hooks/Configuration";
import { Location } from "@advice/hooks/Location";
import { get } from "@util/object";

declare module "react" {
    interface HTMLAttributes<T> {
        on?: string;
    }
}

export const AMPPageHeader: React.FC = () => {
    const { museApiOptions, facebookClientID } = React.useContext(Configuration);
    const { contentAuthor, title } = React.useContext(ArticleContent);
    const { href } = React.useContext(Location);
    const museHost = museApiOptions.host;
    return (
        <>
            <header className="muse-amp-header">
                <span className="muse-amp-header__left">
                    <button aria-label="Menu" className="muse-amp-header__hamburger" on="tap:main-sidebar.toggle">
                        <svg viewBox="0 0 32 24">
                            <path d="M2 0h28a2 2 0 110 4H2a2 2 0 110-4zm0 10h28a2 2 0 110 4H2a2 2 0 110-4zm0 10h28a2 2 0 110 4H2a2 2 0 110-4z" />
                        </svg>
                    </button>
                </span>
                <a href={museHost} className="muse-amp-header__middle" title="Go to themuse.com">
                    <SVGIconTheMuseLogo className="muse-amp-header__logo" />
                </a>
                <span className="muse-amp-header__right">
                    <button
                        id="header-share-button"
                        aria-label="Share"
                        className="muse-amp-header__share"
                        on="tap:share-sidebar.toggle"
                    >
                        Share
                    </button>
                </span>
            </header>
            <form action="/search" className="muse-amp-search-form" target="_top">
                <input type="search" name="keyword" placeholder="Search Jobs and Companies" defaultValue="" />
                <button className="muse-amp-search-form--search-button">
                    <svg
                        role="presentation"
                        className="muse-amp-search-form--magnifying-glass-icon"
                        viewBox="0 0 20 19"
                    >
                        <circle cx="7.65" cy="7.65" r="7.15" />
                        <path strokeLinecap="round" d="M13.333 12.948l4.49 4.784" />
                    </svg>
                </button>
                <input type="hidden" name="searchType" value="jobs" />
            </form>
            <amp-animation id="showShareButton" layout="nodisplay">
                <script
                    type="application/json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify({
                            duration: "200ms",
                            fill: "both",
                            iterations: "1",
                            direction: "alternate",
                            animations: [
                                {
                                    selector: "#header-share-button",
                                    keyframes: [
                                        {
                                            opacity: "1",
                                            visibility: "visible"
                                        }
                                    ]
                                }
                            ]
                        })
                    }}
                />
            </amp-animation>
            <amp-animation id="hideShareButton" layout="nodisplay">
                <script
                    type="application/json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify({
                            duration: "200ms",
                            fill: "both",
                            iterations: "1",
                            direction: "alternate",
                            animations: [
                                {
                                    selector: "#header-share-button",
                                    keyframes: [
                                        {
                                            opacity: "0",
                                            visibility: "hidden"
                                        }
                                    ]
                                }
                            ]
                        })
                    }}
                />
            </amp-animation>
            <amp-position-observer
                target="social-marker"
                on="enter:hideShareButton.start; exit:showShareButton.start"
                layout="nodisplay"
            />
            <amp-sidebar class="muse-amp-nav" id="share-sidebar" layout="nodisplay" side="right">
                <ul>
                    <li className="muse-amp-nav__item">
                        <amp-social-share
                            type="facebook"
                            data-param-app_id={facebookClientID}
                            aria-label="Share on Facebook"
                            class="muse-amp-nav-share"
                            width="208px"
                            height="24px"
                        >
                            <div className="muse-amp-nav-share__icon muse-amp-nav-share__icon--facebook" />
                            <span>Facebook</span>
                        </amp-social-share>
                    </li>
                    <li className="muse-amp-nav__item">
                        <amp-social-share
                            type="twitter"
                            aria-label="Share on Twitter"
                            data-param-text={title}
                            data-param-via={`TheMuse${get(contentAuthor, "twitterHandle", "")}`}
                            class="muse-amp-nav-share"
                            width="208px"
                            height="24px"
                        >
                            <div className="muse-amp-nav-share__icon muse-amp-nav-share__icon--twitter" />
                            <span>Twitter</span>
                        </amp-social-share>
                    </li>
                    <li className="muse-amp-nav__item">
                        <amp-social-share
                            type="linkedin"
                            aria-label="Share on LinkedIn"
                            data-param-source="The Muse"
                            data-param-title={title}
                            class="muse-amp-nav-share"
                            width="208px"
                            height="24px"
                        >
                            <div className="muse-amp-nav-share__icon muse-amp-nav-share__icon--linkedin" />
                            <span>LinkedIn</span>
                        </amp-social-share>
                    </li>
                    <li className="muse-amp-nav__item">
                        <amp-social-share
                            type="email"
                            aria-label="Share via email"
                            data-param-body={`Just read this awesome article I thought I would share with you! Check it out here: ${href}`}
                            class="muse-amp-nav-share"
                            width="208px"
                            height="24px"
                        >
                            <div className="muse-amp-nav-share__icon muse-amp-nav-share__icon--email" />
                            <span>Email</span>
                        </amp-social-share>
                    </li>
                </ul>
            </amp-sidebar>
            <amp-sidebar class="muse-amp-nav" id="main-sidebar" layout="nodisplay" side="left">
                <ul>
                    <li className="muse-amp-nav__item">
                        <a href={`${museHost}/jobs?ref=amp`}>Jobs</a>
                    </li>
                    <li className="muse-amp-nav__item">
                        <a href={`${museHost}/companies?ref=amp`}>Companies</a>
                    </li>
                    <li className="muse-amp-nav__item">
                        <a href={`${museHost}/advice?ref=amp`}>Advice</a>
                    </li>
                    <li className="muse-amp-nav__item">
                        <a href={`${museHost}/coaching?ref=amp`}>Coaching</a>
                    </li>
                    <li className="muse-amp-nav__item muse-amp-nav__item--blue">
                        <a href={`${museHost}/?showAuth=signUp&ref=amp`}>Sign In / Sign Up</a>
                    </li>
                </ul>
            </amp-sidebar>
        </>
    );
};
